import {
  DEFAULT_UI_CONFIGURATION,
  MyLicensesConfiguration,
  LocalizationLocale,
} from "./my-licenses-configuration";
import cloneDeep from "lodash/cloneDeep";
import { compareVersions } from "../util/semantic-version";

/**
 * Utility for resolving a value for a section feature when merging ui conf with defaults
 * @param uiConf Provided ui conf
 * @param defaults Default ui conf for requested conf version
 * @param section Name of section
 * @param feature Name of feature
 */
function resolveSectionFeature(
    uiConf: MyLicensesConfiguration,
    defaults: MyLicensesConfiguration,
    section: string,
    feature: string
):
    | boolean
    | string
    | LocalizationLocale[] {
  let retVal: boolean;
  // Not sure what this does but apparently this is somehow better than untyped js, I'm not really seeing it though.
  type SectionKey = keyof typeof uiConf.functionality;
  const sectionAsSectionKey = section as SectionKey;
  if (
      uiConf.functionality &&
      uiConf.functionality[sectionAsSectionKey] &&
      uiConf.functionality[sectionAsSectionKey][feature] !== undefined
  ) {
    retVal = uiConf.functionality[sectionAsSectionKey][feature];
  } else if (
      defaults.functionality &&
      defaults.functionality[sectionAsSectionKey] &&
      defaults.functionality[sectionAsSectionKey][feature] !== undefined
  ) {
    retVal = defaults.functionality[sectionAsSectionKey][feature];
  } else {
    // theoretical, should never happen
    retVal = true;
  }
  return retVal;
}
/**
 * Utility for resolving default values matching the version of the used ui configuration
 * @param version
 */
function resolveDefaults(version: string): MyLicensesConfiguration {
  let uiConf = cloneDeep(DEFAULT_UI_CONFIGURATION);
  if (
    compareVersions(DEFAULT_UI_CONFIGURATION.version as string, version) > 0
  ) {
    // old version
    if (compareVersions("0.0.1", version || "") > 0) {
      // version is older than 0.0.1
    }
  }
  return uiConf;
}

function migrateConfTo_0_0_1(
  conf: MyLicensesConfiguration
): MyLicensesConfiguration {
  let uiConf = cloneDeep(conf);
  if (compareVersions("0.0.1", uiConf.version || "") > 0) {
    uiConf.version = "0.1.0";
  }
  return uiConf;
}

export function migrateConfToLatest(
  conf: MyLicensesConfiguration
): MyLicensesConfiguration {
  let uiConf = cloneDeep(conf);
  const inputIsEmpty = Object.keys(uiConf).length === 0;
  if (!inputIsEmpty) {
    const versionComparisonResult = compareVersions(
      DEFAULT_UI_CONFIGURATION.version as string,
      uiConf.version || ""
    );
    if (versionComparisonResult > 0) {
      if (process.env.NODE_ENV !== "production") {
        console.warn(
          "UIConfiguration version is outdated (version: %o). Please migrate the configuration to latest (version %o).",
          uiConf.version,
          DEFAULT_UI_CONFIGURATION.version
        );
      }
      uiConf = migrateConfTo_0_0_1(uiConf);
    } else if (versionComparisonResult < 0) {
      throw new Error(
        "Configuration version (" +
          uiConf.version +
          ") not supported. Latest version is (" +
          DEFAULT_UI_CONFIGURATION.version +
          ")"
      );
    }
  } else {
    // empty input means "use latest defaults"
    uiConf.version = DEFAULT_UI_CONFIGURATION.version;
  }
  return uiConf;
}

export function mergeInDefaults(
  uiConf: MyLicensesConfiguration,
  version: string
): MyLicensesConfiguration {
  // resolve the defaults for the conf version
  const defaults = resolveDefaults(version);
  const conf: MyLicensesConfiguration = {
    version: uiConf.version,
    functionality: {
      expiringInMs: uiConf.functionality?.expiringInMs !== null && uiConf.functionality?.expiringInMs !== undefined ?
          uiConf.functionality?.expiringInMs :
          defaults.functionality?.expiringInMs,
      menu: {
        links: {
         ...(uiConf.functionality?.menu?.links || defaults.functionality?.menu?.links)
        },
      },
      "my-licenses": {
        "date-format": resolveSectionFeature(
            uiConf,
            defaults,
            "my-licenses",
            "date-format"
        ) as string,
      },
      additionalLicenseDataURI:
          uiConf.functionality?.additionalLicenseDataURI !== undefined &&
          uiConf.functionality?.additionalLicenseDataURI !== null
              ? uiConf.functionality.additionalLicenseDataURI
              : defaults.functionality?.additionalLicenseDataURI,
      localization: {
        "show-switcher": resolveSectionFeature(
          uiConf,
          defaults,
          "localization",
          "show-switcher"
        ) as boolean,
        default: resolveSectionFeature(
          uiConf,
          defaults,
          "localization",
          "default"
        ) as string,
        autoDetect: resolveSectionFeature(
            uiConf,
            defaults,
            "localization",
            "autoDetect"
        ) as boolean,
        translationsUrl: resolveSectionFeature(
          uiConf,
          defaults,
          "localization",
          "translationsUrl"
        ) as string,
        locales: resolveSectionFeature(
          uiConf,
          defaults,
          "localization",
          "locales"
        ) as LocalizationLocale[],
      },
    },
    security: {
      idTokenIatLeeway:
        uiConf.security?.idTokenIatLeeway !== undefined &&
        uiConf.security?.idTokenIatLeeway !== null
          ? uiConf.security.idTokenIatLeeway
          : defaults.security?.idTokenIatLeeway,
    },
  };
  return conf;
}
